/**
 * OwnerDashboard — Panel Interno del Propietario (Melver)
 * Acceso: solo melver2880@gmail.com + PIN de sesión
 * URL: /owner
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import apiClient from "../lib/api";
import {
  Shield, Users, CheckCircle, Clock, AlertTriangle, TrendingDown,
  CreditCard, FileText, DollarSign, Download, Mail, RefreshCw,
  Search, ChevronUp, ChevronDown, ChevronsLeft, ChevronsRight,
  ChevronLeft, ChevronRight, X, Check, FlaskConical, Ban,
  Eye, RotateCcw, Gift, UserX, Bell, Filter, BarChart2, LogOut, ShieldOff, ShieldPlus,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";

const OWNER_EMAIL = "melver2880@gmail.com";

// ── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, daysRemaining }) {
  const cfg = {
    active:    { label: "Activo",       cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" },
    expiring:  { label: `Vence ${daysRemaining != null ? `en ${Math.max(0,Math.round(daysRemaining))}d` : "pronto"}`, cls: "bg-amber-500/15 text-amber-400 border-amber-500/25" },
    expired:   { label: daysRemaining != null ? `Venció hace ${Math.abs(Math.round(daysRemaining))}d` : "Vencido", cls: "bg-red-500/15 text-red-400 border-red-500/25" },
    suspended: { label: "Suspendido",   cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/25" },
  }[status] || { label: status, cls: "bg-zinc-700 text-zinc-300" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${cfg.cls}`}>{cfg.label}</span>;
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ icon: Icon, label, value, sub, color, loading }) {
  const colors = {
    green:  "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    amber:  "text-amber-400   bg-amber-500/10   border-amber-500/20",
    red:    "text-red-400     bg-red-500/10     border-red-500/20",
    blue:   "text-blue-400    bg-blue-500/10    border-blue-500/20",
    purple: "text-purple-400  bg-purple-500/10  border-purple-500/20",
    zinc:   "text-zinc-400    bg-zinc-500/10    border-zinc-500/20",
    teal:   "text-teal-400    bg-teal-500/10    border-teal-500/20",
  }[color] || "text-zinc-400 bg-zinc-500/10 border-zinc-500/20";
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-2 ${colors}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
        <Icon className="w-4 h-4 opacity-60" />
      </div>
      {loading ? (
        <div className="h-8 w-16 rounded bg-white/5 animate-pulse" />
      ) : (
        <div className="text-2xl sm:text-3xl font-black leading-none">{value ?? "—"}</div>
      )}
      {sub && <div className="text-[10px] opacity-50">{sub}</div>}
    </div>
  );
}

// ── Pin Screen ────────────────────────────────────────────────────────────────
function PinScreen({ onSuccess }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState(false);
  const PIN = "864321";
  const inputRef = useRef(null);

  // Auto-focus al montar (con delay para móvil)
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 250);
    return () => clearTimeout(t);
  }, []);

  const check = (val) => {
    const v = (val ?? pin).trim();
    if (v === PIN) {
      try { localStorage.setItem("owner_pin_ok", String(Date.now())); } catch {}
      onSuccess();
    } else if (v.length >= 6) {
      setErr(true); setPin(""); setTimeout(() => setErr(false), 1200);
      inputRef.current?.focus();
    }
  };

  const handleChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(v);
    setErr(false);
    if (v.length === 6) check(v); // auto-submit cuando complete
  };

  return (
    <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center px-4"
         onClick={() => inputRef.current?.focus()}>
      <div className="w-full max-w-xs space-y-6 text-center">
        <Shield className="w-12 h-12 text-amber-400 mx-auto" />
        <div>
          <div className="text-white font-bold text-lg">Panel del Propietario</div>
          <div className="text-zinc-500 text-sm mt-1">Toca el campo y escribe tu PIN</div>
        </div>
        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          value={pin}
          onChange={handleChange}
          onKeyDown={e => e.key === "Enter" && check()}
          placeholder="• • • • • •"
          className={`w-full text-center text-3xl tracking-[0.5em] py-4 rounded-xl bg-zinc-900 border-2 ${err ? "border-red-500" : "border-amber-500/40"} text-white outline-none focus:border-amber-500 transition`}
          data-testid="owner-pin-input"
          autoFocus
        />
        <button onClick={() => check()} disabled={pin.length < 4}
          data-testid="owner-pin-submit"
          className="w-full py-4 rounded-xl bg-amber-500 text-black font-bold text-base hover:bg-amber-400 transition disabled:opacity-40">
          Entrar
        </button>
        {err && <p className="text-red-400 text-sm">PIN incorrecto, intenta de nuevo</p>}
      </div>
    </div>
  );
}

// ── Renew Modal ───────────────────────────────────────────────────────────────
function RenewModal({ user, onClose, onSuccess }) {
  const [days, setDays] = useState(30);
  const [amount, setAmount] = useState(160);
  const [method, setMethod] = useState("zelle");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const presets = [{ d: 7, a: 50, label: "7 días — $50" }, { d: 30, a: 160, label: "30 días — $160" }];
  const submit = async () => {
    setLoading(true);
    try {
      const r = await apiClient.post(`/owner/users/${user.id}/renew`, { days, amount, method, notes });
      toast.success(r.data.message);
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al renovar");
    }
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={onClose}>
      <div className="w-full max-w-md bg-[#111113] border border-zinc-800 rounded-2xl p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-bold text-white text-lg">Renovar manualmente</div>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-500" /></button>
        </div>
        <div className="text-sm text-zinc-400">
          <span className="font-semibold text-white">{user.name || user.email}</span>
          <span className="ml-2 text-zinc-600">{user.email}</span>
        </div>
        <div className="flex gap-2">
          {presets.map(p => (
            <button key={p.d} onClick={() => { setDays(p.d); setAmount(p.a); }}
              className={`flex-1 py-2 rounded-lg text-xs font-bold transition border ${days === p.d ? "bg-amber-500/20 border-amber-500/50 text-amber-400" : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-600"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Días a agregar</label>
            <input type="number" value={days} min={1} max={365} onChange={e => setDays(+e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Monto cobrado ($)</label>
            <input type="number" value={amount} min={0} step="0.01" onChange={e => setAmount(+e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-500" />
          </div>
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Método de pago</label>
          <select value={method} onChange={e => setMethod(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-500">
            <option value="zelle">Zelle</option>
            <option value="pago_movil">Pago Móvil</option>
            <option value="binance">Binance</option>
            <option value="efectivo">Efectivo</option>
            <option value="stripe">Stripe</option>
            <option value="otro">Otro</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Notas (opcional)</label>
          <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Ej: Pago por Zelle confirmado"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-500" />
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-zinc-800 text-zinc-300 text-sm font-semibold hover:bg-zinc-700 transition">Cancelar</button>
          <button onClick={submit} disabled={loading}
            className="flex-1 py-2 rounded-lg bg-amber-500 text-black text-sm font-bold hover:bg-amber-400 transition disabled:opacity-50">
            {loading ? "Renovando…" : `Renovar ${days}d por $${amount}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── User Row Actions ──────────────────────────────────────────────────────────
function UserRow({ u, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [showRenew, setShowRenew] = useState(false);
  const [busy, setBusy] = useState(false);

  const action = async (endpoint, method = "post", body = {}) => {
    setBusy(true);
    try {
      const r = await apiClient[method](`/owner/users/${u.id}/${endpoint}`, body);
      toast.success(r.data.message || "Acción realizada");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return format(parseISO(iso), "dd/MM/yyyy HH:mm", { locale: es }); }
    catch { return iso.slice(0, 16).replace("T", " "); }
  };

  return (
    <>
      <tr className="border-b border-zinc-800/50 hover:bg-white/[0.02] transition-colors">
        <td className="px-3 py-2.5">
          <div className="text-sm font-semibold text-white truncate max-w-[130px]">{u.name || "—"}</div>
          <div className="text-[11px] text-zinc-500 truncate max-w-[130px]">{u.email}</div>
        </td>
        <td className="px-3 py-2.5">
          <StatusBadge status={u.display_status} daysRemaining={u.days_remaining} />
        </td>
        <td className="px-3 py-2.5 text-xs text-zinc-400 hidden sm:table-cell">{fmtDate(u.subscription_expires_at)}</td>
        <td className="px-3 py-2.5 hidden md:table-cell">
          {u.days_remaining != null ? (
            <span className={`text-xs font-semibold ${u.days_remaining > 7 ? "text-emerald-400" : u.days_remaining > 0 ? "text-amber-400" : "text-red-400"}`}>
              {u.days_remaining > 0 ? `${Math.round(u.days_remaining)}d` : `−${Math.abs(Math.round(u.days_remaining))}d`}
            </span>
          ) : "—"}
        </td>
        <td className="px-3 py-2.5 hidden lg:table-cell">
          <span className="text-xs text-zinc-500">{u.payment_method_display || "—"}</span>
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            <button onClick={() => setShowRenew(true)} disabled={busy}
              data-testid={`renew-btn-${u.id}`}
              title="Renovar manualmente" className="p-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 transition">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => action("extend", "post", { days: 7 })} disabled={busy}
              title="+7 días gratis" className="p-1.5 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 transition">
              <Gift className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { if (window.confirm(`¿Desactivar a ${u.email}?`)) action("deactivate"); }} disabled={busy}
              title="Desactivar" className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition">
              <UserX className="w-3.5 h-3.5" />
            </button>
            {u.role === "admin" && (
              <button onClick={() => { if (window.confirm(`⚠️ REVOCAR ACCESO ADMIN de ${u.email}?\n\nEsto quitará el rol de admin y suspenderá la cuenta.`)) action("revoke-access"); }} disabled={busy}
                title="Revocar acceso admin" className="p-1.5 rounded-lg bg-rose-600/20 hover:bg-rose-600/40 text-rose-400 transition border border-rose-600/30"
                data-testid={`revoke-admin-btn-${u.id}`}>
                <ShieldOff className="w-3.5 h-3.5" />
              </button>
            )}
            {u.role !== "admin" && (
              <button onClick={() => { if (window.confirm(`¿Promover a ${u.email} a ADMINISTRADOR?\n\nTendrá acceso al panel admin y podrá crear picks, ver usuarios y todo lo del panel.`)) action("promote-admin"); }} disabled={busy}
                title="Promover a admin" className="p-1.5 rounded-lg bg-amber-600/20 hover:bg-amber-600/40 text-amber-300 transition border border-amber-600/30"
                data-testid={`promote-admin-btn-${u.id}`}>
                <ShieldPlus className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => action("remind")} disabled={busy}
              title="Enviar recordatorio" className="p-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 transition">
              <Bell className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => action("mark-test", "patch", { is_test_user: !u.is_test_user })} disabled={busy}
              title={u.is_test_user ? "Quitar flag test" : "Marcar como test"}
              className={`p-1.5 rounded-lg transition ${u.is_test_user ? "bg-purple-500/20 text-purple-300" : "bg-zinc-700 text-zinc-500 hover:text-zinc-300"}`}>
              <FlaskConical className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
      {showRenew && <tr><td colSpan={6}><RenewModal user={u} onClose={() => setShowRenew(false)} onSuccess={() => { setShowRenew(false); onRefresh(); }} /></td></tr>}
    </>
  );
}

// ── Usuarios Tab ──────────────────────────────────────────────────────────────
function UsersTab() {
  const [data, setData] = useState({ users: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterBy, setFilterBy] = useState("all");
  const [sortBy, setSortBy] = useState("subscription_expires_at");
  const [sortDir, setSortDir] = useState(1);
  const [search, setSearch] = useState("");
  const [showTest, setShowTest] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: opts.page ?? page,
        per_page: 50,
        filter_by: opts.filterBy ?? filterBy,
        sort_by: opts.sortBy ?? sortBy,
        sort_dir: opts.sortDir ?? sortDir,
        search: opts.search ?? search,
        show_test: opts.showTest ?? showTest,
      });
      const r = await apiClient.get(`/owner/users?${params}`);
      setData(r.data);
    } catch {
      toast.error("Error al cargar usuarios");
    }
    setLoading(false);
  }, [page, filterBy, sortBy, sortDir, search, showTest]);

  useEffect(() => { load(); }, [page, filterBy, sortBy, sortDir, showTest]);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load({ page: 1, search: val }); }, 350);
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => { const nd = d === 1 ? -1 : 1; load({ sortDir: nd }); return nd; });
    else { setSortBy(col); setSortDir(1); load({ sortBy: col, sortDir: 1 }); }
  };

  const SortIcon = ({ col }) => sortBy === col
    ? (sortDir === 1 ? <ChevronUp className="w-3 h-3 inline ml-0.5" /> : <ChevronDown className="w-3 h-3 inline ml-0.5" />)
    : null;

  const FILTERS = [
    ["all", "Todos"], ["active", "Activos"], ["expiring7d", "Vencen 7d"], ["expired", "Vencidos"],
    ["suspended", "Suspendidos"], ["manual", "Manual"], ["stripe", "Stripe"], ["new30d", "Nuevos 30d"]
  ];

  const start = (page - 1) * 50 + 1;
  const end   = Math.min(page * 50, data.total);

  return (
    <div className="space-y-4">
      {/* Filters row */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-shrink-0">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            ref={searchRef}
            type="text" value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="Buscar nombre o email…"
            data-testid="owner-user-search"
            className="pl-8 pr-3 py-2 text-xs bg-zinc-900 border border-zinc-700 rounded-lg text-white outline-none focus:border-amber-500 w-52 transition"
          />
          {search && <button onClick={() => handleSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"><X className="w-3 h-3" /></button>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([val, lbl]) => (
            <button key={val} onClick={() => { setFilterBy(val); setPage(1); load({ filterBy: val, page: 1 }); }}
              data-testid={`filter-${val}`}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition border ${filterBy === val ? "bg-amber-500 text-black border-amber-500" : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-600"}`}>
              {lbl}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-zinc-500 cursor-pointer ml-auto">
          <input type="checkbox" checked={showTest} onChange={e => { setShowTest(e.target.checked); setPage(1); load({ showTest: e.target.checked, page: 1 }); }} />
          Mostrar test
        </label>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-800 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-zinc-500">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Cargando usuarios…</span>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/60">
                <th className="text-left px-3 py-2.5 text-[10px] text-zinc-500 font-bold uppercase cursor-pointer" onClick={() => handleSort("name")}>
                  Usuario <SortIcon col="name" />
                </th>
                <th className="text-left px-3 py-2.5 text-[10px] text-zinc-500 font-bold uppercase cursor-pointer" onClick={() => handleSort("display_status")}>
                  Estado <SortIcon col="display_status" />
                </th>
                <th className="text-left px-3 py-2.5 text-[10px] text-zinc-500 font-bold uppercase hidden sm:table-cell cursor-pointer" onClick={() => handleSort("subscription_expires_at")}>
                  Vencimiento <SortIcon col="subscription_expires_at" />
                </th>
                <th className="text-left px-3 py-2.5 text-[10px] text-zinc-500 font-bold uppercase hidden md:table-cell cursor-pointer" onClick={() => handleSort("days_remaining")}>
                  Días <SortIcon col="days_remaining" />
                </th>
                <th className="text-left px-3 py-2.5 text-[10px] text-zinc-500 font-bold uppercase hidden lg:table-cell">
                  Método
                </th>
                <th className="text-left px-3 py-2.5 text-[10px] text-zinc-500 font-bold uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.users.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-zinc-600 text-sm">Sin usuarios en este filtro</td></tr>
              ) : (
                data.users.map(u => <UserRow key={u.id} u={u} onRefresh={() => load()} />)
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data.total_pages > 1 && (
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>Mostrando {start}–{end} de {data.total} usuarios</span>
          <div className="flex items-center gap-1">
            <button onClick={() => { setPage(1); load({ page: 1 }); }} disabled={page === 1} className="p-1.5 rounded disabled:opacity-30 hover:bg-zinc-800 transition"><ChevronsLeft className="w-3.5 h-3.5" /></button>
            <button onClick={() => { const p = page - 1; setPage(p); load({ page: p }); }} disabled={page === 1} className="p-1.5 rounded disabled:opacity-30 hover:bg-zinc-800 transition"><ChevronLeft className="w-3.5 h-3.5" /></button>
            {Array.from({ length: Math.min(5, data.total_pages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2 + i, data.total_pages - 4 + i));
              return (
                <button key={p} onClick={() => { setPage(p); load({ page: p }); }}
                  className={`w-7 h-7 rounded text-xs font-semibold transition ${p === page ? "bg-amber-500 text-black" : "hover:bg-zinc-800"}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => { const p = page + 1; setPage(p); load({ page: p }); }} disabled={page === data.total_pages} className="p-1.5 rounded disabled:opacity-30 hover:bg-zinc-800 transition"><ChevronRight className="w-3.5 h-3.5" /></button>
            <button onClick={() => { setPage(data.total_pages); load({ page: data.total_pages }); }} disabled={page === data.total_pages} className="p-1.5 rounded disabled:opacity-30 hover:bg-zinc-800 transition"><ChevronsRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}
      {data.total > 0 && data.total_pages === 1 && (
        <div className="text-xs text-zinc-600">{data.total} usuarios</div>
      )}
    </div>
  );
}

// ── Logs Tab ──────────────────────────────────────────────────────────────────
function LogsTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/audit-log", { headers: { Authorization: `Bearer ${token}` } });
      setLogs(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch {
      toast.error("Error al cargar logs");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = logs.filter(l => {
    if (filter === "ronald") return l.admin_email !== OWNER_EMAIL;
    if (filter === "me") return l.admin_email === OWNER_EMAIL;
    return true;
  }).filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.target_user_email || "").toLowerCase().includes(s) ||
           (l.target_user_name || "").toLowerCase().includes(s) ||
           (l.admin_email || "").toLowerCase().includes(s);
  });

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return format(parseISO(iso), "dd/MM/yy HH:mm", { locale: es }); }
    catch { return iso.slice(0, 16).replace("T", " "); }
  };

  const actionColors = {
    manual_renewal: "text-amber-400",
    free_extension: "text-teal-400",
    user_deactivated: "text-red-400",
    reminder_sent: "text-blue-400",
    user_suspended: "text-orange-400",
    user_unsuspended: "text-emerald-400",
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar usuario…"
            className="pl-8 pr-3 py-2 text-xs bg-zinc-900 border border-zinc-700 rounded-lg text-white outline-none focus:border-amber-500 w-48 transition" />
        </div>
        {[["all","Todos"],["me","Solo yo"],["ronald","Solo Ronald"]].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition ${filter===v?"bg-amber-500 text-black border-amber-500":"bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-600"}`}>
            {l}
          </button>
        ))}
        <span className="ml-auto text-xs text-zinc-600">{filtered.length} registros</span>
        <button onClick={load} className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white transition"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      <div className="rounded-xl border border-zinc-800 overflow-x-auto">
        {loading ? (
          <div className="py-12 flex items-center justify-center gap-2 text-zinc-500"><RefreshCw className="w-4 h-4 animate-spin" /><span className="text-sm">Cargando…</span></div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/60 text-[10px] text-zinc-500 uppercase">
                <th className="text-left px-3 py-2.5">Fecha</th>
                <th className="text-left px-3 py-2.5">Admin</th>
                <th className="text-left px-3 py-2.5">Acción</th>
                <th className="text-left px-3 py-2.5">Usuario afectado</th>
                <th className="text-left px-3 py-2.5 hidden md:table-cell">Detalles</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-zinc-600">Sin registros</td></tr>
              ) : filtered.map((l, i) => (
                <tr key={l.id || i} className="border-b border-zinc-800/40 hover:bg-white/[0.015]">
                  <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="px-3 py-2 text-zinc-300">{l.admin_email === OWNER_EMAIL ? <span className="text-amber-400 font-bold">Melver</span> : <span className="text-blue-400">Ronald</span>}</td>
                  <td className={`px-3 py-2 font-semibold whitespace-nowrap ${actionColors[l.action_type] || "text-zinc-300"}`}>{l.action_label || l.action_type}</td>
                  <td className="px-3 py-2">
                    <div className="text-zinc-300">{l.target_user_name || "—"}</div>
                    <div className="text-zinc-600">{l.target_user_email}</div>
                  </td>
                  <td className="px-3 py-2 text-zinc-500 hidden md:table-cell max-w-xs truncate">
                    {typeof l.details === "object" ? Object.entries(l.details || {}).map(([k,v]) => `${k}: ${v}`).join(" · ") : (l.details || "")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Reportes Tab ──────────────────────────────────────────────────────────────
function ReportesTab({ token }) {
  const [sendingReport, setSendingReport] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const downloadCsv = (url) => {
    const a = document.createElement("a");
    a.href = `${apiBase}/api${url}?token=${token}`;
    a.click();
  };

  const triggerReport = async () => {
    setSendingReport(true);
    try {
      const r = await apiClient.post("/owner/send-daily-report");
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar reporte");
    }
    setSendingReport(false);
  };

  const ReportCard = ({ icon: Icon, title, desc, action, color = "amber" }) => (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color==="amber"?"bg-amber-500/10 text-amber-400":color==="blue"?"bg-blue-500/10 text-blue-400":"bg-green-500/10 text-green-400"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-white">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
        <button onClick={action} className={`mt-3 px-4 py-1.5 rounded-lg text-xs font-bold transition ${color==="amber"?"bg-amber-500 hover:bg-amber-400 text-black":color==="blue"?"bg-blue-600 hover:bg-blue-500 text-white":"bg-emerald-600 hover:bg-emerald-500 text-white"}`}>
          {title.includes("Enviar") ? (sendingReport ? "Enviando…" : title) : "Descargar"}
        </button>
      </div>
    </div>
  );

  const setupAiBot = async () => {
    if (!window.confirm("¿Crear o resetear el usuario AI Bot?\n\nEsto creará (o reseteará) el usuario ai-bot@ganasconronald.com con rol admin y plan premium. La contraseña quedará en AiBot2026Picks.")) return;
    try {
      const r = await apiClient.post("/owner/ai-bot/setup");
      const data = r.data;
      window.prompt(
        `✅ ${data.message}\n\nGuarda estas credenciales en un lugar seguro:\n\nEmail: ${data.email}\n\nContraseña (selecciona y copia):`,
        data.password
      );
      toast.success(data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error configurando AI Bot");
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-bold mb-2">Exports CSV</div>
      <ReportCard icon={Download} title="Exportar usuarios" desc="Todos los usuarios con estado, vencimiento y método de pago. Excel compatible (UTF-8)." action={() => downloadCsv("/owner/export/users.csv")} color="amber" />
      <ReportCard icon={FileText} title="Exportar logs" desc="Historial completo de todas las acciones administrativas." action={() => downloadCsv("/owner/export/logs.csv")} color="blue" />
      <div className="text-xs text-zinc-500 uppercase tracking-widest font-bold mt-6 mb-2">Alertas por Email</div>
      <ReportCard icon={Mail} title="Enviar reporte diario ahora" desc={`Envía el reporte completo de hoy a ${OWNER_EMAIL}. Incluye: vencidos hoy, próximos 7 días, nuevos usuarios e ingresos.`} action={triggerReport} color="green" />

      <div className="text-xs text-zinc-500 uppercase tracking-widest font-bold mt-6 mb-2">Integraciones IA</div>
      <div className="rounded-xl border-2 border-amber-500/40 bg-amber-500/5 p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-500/15 text-amber-300">
          <Shield className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white">Crear o resetear AI Bot</div>
          <div className="text-xs text-zinc-500 mt-0.5">Usuario técnico para que tu agente de IA externo cree picks vía API. Toca el botón y se crea/resetea con contraseña conocida. Idempotente.</div>
          <button
            onClick={setupAiBot}
            data-testid="setup-ai-bot-btn"
            className="mt-3 px-5 py-2 rounded-lg text-xs font-black uppercase tracking-wider bg-amber-500 hover:bg-amber-400 text-black transition">
            🤖 Configurar AI Bot
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-4 text-xs text-zinc-500 space-y-1">
        <div className="font-semibold text-zinc-400">Alertas automáticas activas:</div>
        <div>✅ Email diario a las <span className="text-white">9:00 AM UTC</span> con vencimientos y revenue</div>
        <div>📧 Destinatario: <span className="text-white">{OWNER_EMAIL}</span></div>
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
function Dashboard({ token }) {
  const [kpis, setKpis] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [tab, setTab] = useState("dashboard");
  const [ownerData, setOwnerData] = useState(null);

  const loadKpis = useCallback(async () => {
    setKpiLoading(true);
    try {
      const r = await apiClient.get("/owner/kpis");
      setKpis(r.data);
    } catch {
      toast.error("Error al cargar KPIs");
    }
    setKpiLoading(false);
  }, []);

  const loadOwnerDashboard = useCallback(async () => {
    try {
      const r = await apiClient.get("/owner/dashboard", { headers: { Authorization: `Bearer ${token}` } });
      setOwnerData(r.data);
    } catch {}
  }, [token]);

  useEffect(() => {
    loadKpis();
    loadOwnerDashboard();
  }, [loadKpis, loadOwnerDashboard]);

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart2 },
    { id: "usuarios",  label: "Usuarios",  icon: Users },
    { id: "logs",      label: "Logs",      icon: FileText },
    { id: "reportes",  label: "Reportes",  icon: Download },
  ];

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return format(parseISO(iso), "dd/MM/yyyy", { locale: es }); }
    catch { return iso.slice(0, 10); }
  };

  return (
    <div className="min-h-screen bg-[#0A0B0D] text-white">
      {/* Header */}
      <div className="border-b border-zinc-800 sticky top-0 z-30 bg-[#0A0B0D]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-sm font-black text-white">Panel Propietario</div>
              <div className="text-[10px] text-zinc-600">Solo Melver · {new Date().toLocaleDateString("es-ES")}</div>
            </div>
          </div>
          <button onClick={() => { sessionStorage.removeItem("owner_auth"); window.location.reload(); }}
            className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-red-400 transition">
            <LogOut className="w-3.5 h-3.5" />
            Salir
          </button>
        </div>
        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto pb-0">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`owner-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition ${tab===t.id?"border-amber-500 text-amber-400":"border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* ── Dashboard Tab ── */}
        {tab === "dashboard" && (
          <div className="space-y-6">
            {/* KPI Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <KPICard icon={CheckCircle} label="Activos ahora"       value={kpis?.active_count}    color="green"  sub="con membresía activa"     loading={kpiLoading} />
              <KPICard icon={Clock}       label="Vencen en 7 días"    value={kpis?.expiring7d_count} color="amber"  sub="atención inmediata"       loading={kpiLoading} />
              <KPICard icon={AlertTriangle} label="Vencidos"          value={kpis?.expired_count}   color="red"    sub="sin membresía activa"     loading={kpiLoading} />
              <KPICard icon={DollarSign}  label="Revenue del mes"     value={kpis ? `$${kpis.revenue_month.toFixed(0)}` : null} color="teal"  sub="pagos confirmados"        loading={kpiLoading} />
              <KPICard icon={Users}       label="Nuevos hoy"          value={kpis?.new_today}       color="blue"   sub="registros de hoy"         loading={kpiLoading} />
              <KPICard icon={TrendingDown} label="Tasa de renovación" value={kpis ? `${kpis.renewal_rate}%` : null} color="purple" sub={`de ${kpis?.renewal_base ?? 0} expirados 30d`} loading={kpiLoading} />
              <KPICard icon={Users}       label="Total usuarios"      value={kpis?.total_users}     color="zinc"   sub="excluye test y admins"    loading={kpiLoading} />
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 flex flex-col gap-1 justify-center cursor-pointer hover:bg-amber-500/10 transition" onClick={loadKpis}>
                <RefreshCw className="w-4 h-4 text-amber-400" />
                <div className="text-[10px] text-amber-500 font-bold uppercase tracking-widest">Actualizar</div>
                <div className="text-[10px] text-zinc-600">Recargar KPIs</div>
              </div>
            </div>

            {/* Alert: expiring today */}
            {kpis?.expiring7d_count > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-5 py-3 flex items-center gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <div className="text-sm text-amber-300">
                  <strong>{kpis.expiring7d_count}</strong> usuario(s) vencen en los próximos 7 días — revisa la pestaña <strong>Usuarios</strong> → filtro "Vencen 7d"
                </div>
              </div>
            )}

            {/* Revenue mini-chart (last 30d) */}
            {kpis?.daily_chart?.length > 0 && (
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
                <div className="text-xs text-zinc-500 uppercase tracking-widest font-bold mb-3">Revenue últimos 30 días</div>
                <div className="flex items-end gap-1 h-16">
                  {kpis.daily_chart.map((d, i) => {
                    const maxRev = Math.max(...kpis.daily_chart.map(x => x.revenue), 1);
                    const pct = Math.max((d.revenue / maxRev) * 100, d.revenue > 0 ? 8 : 2);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: $${d.revenue}`}>
                        <div className="w-full rounded-t-sm" style={{ height: `${pct}%`, background: d.revenue > 0 ? "rgba(245,158,11,0.65)" : "rgba(255,255,255,0.05)", minHeight: 2 }} />
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-zinc-600 mt-1 text-right">Total: ${kpis.daily_chart.reduce((s, d) => s + d.revenue, 0).toFixed(0)}</div>
              </div>
            )}

            {/* Quick sections from old dashboard */}
            {ownerData && (
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Vencen hoy */}
                {(ownerData.expiring_today || []).length > 0 && (
                  <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
                    <div className="text-xs font-bold text-red-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Vencen HOY ({ownerData.expiring_today.length})
                    </div>
                    {ownerData.expiring_today.map((u, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-zinc-800/50 last:border-0">
                        <div>
                          <div className="text-sm font-semibold text-white">{u.name || "—"}</div>
                          <div className="text-[11px] text-zinc-500">{u.email}</div>
                        </div>
                        <span className="text-xs text-red-400 font-bold">{fmtDate(u.subscription_expires_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Vencen 7d */}
                {(ownerData.expiring_7d || []).length > 0 && (
                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                    <div className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      Vencen en 7 días ({ownerData.expiring_7d.length})
                    </div>
                    {ownerData.expiring_7d.map((u, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-zinc-800/50 last:border-0">
                        <div>
                          <div className="text-sm font-semibold text-white">{u.name || "—"}</div>
                          <div className="text-[11px] text-zinc-500">{u.email}</div>
                        </div>
                        <span className="text-xs text-amber-400 font-bold">{fmtDate(u.subscription_expires_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Usuarios Tab ── */}
        {tab === "usuarios" && <UsersTab />}

        {/* ── Logs Tab ── */}
        {tab === "logs" && <LogsTab token={token} />}

        {/* ── Reportes Tab ── */}
        {tab === "reportes" && <ReportesTab token={token} />}
      </div>
    </div>
  );
}

// ── Page Root ─────────────────────────────────────────────────────────────────
export default function OwnerDashboard() {
  const { user } = useAuth();
  const token = localStorage.getItem("pv_token");
  // Persistente 24h: lee de localStorage, expira solo
  const [authed, setAuthed] = useState(() => {
    if (sessionStorage.getItem("owner_auth") === "1") return true;
    try {
      const ts = parseInt(localStorage.getItem("owner_pin_ok") || "0", 10);
      return ts && (Date.now() - ts) < 24 * 60 * 60 * 1000;
    } catch { return false; }
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Shield className="w-12 h-12 text-zinc-700 mx-auto" />
          <p className="text-zinc-500 text-sm">Debes iniciar sesión primero</p>
          <a href="/login" className="inline-block px-4 py-2 bg-amber-500 text-black rounded-lg font-bold text-sm">Ir al Login</a>
        </div>
      </div>
    );
  }

  if (user.email !== OWNER_EMAIL) {
    return (
      <div className="min-h-screen bg-[#0A0B0D] flex items-center justify-center">
        <p className="text-zinc-600 text-sm">Página no encontrada</p>
      </div>
    );
  }

  if (!authed) {
    return <PinScreen onSuccess={() => { sessionStorage.setItem("owner_auth", "1"); setAuthed(true); }} />;
  }

  return <Dashboard token={token} />;
}
